// Shared constants, types and label maps for the location-based
// availability & pricing system. UI-only helpers — no design changes.

export type AustralianState = "QLD" | "NSW" | "VIC" | "SA";

export const STATE_OPTIONS: { value: AustralianState; label: string }[] = [
  { value: "QLD", label: "Queensland" },
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "SA", label: "South Australia" },
];

export const STATE_LABELS: Record<AustralianState, string> = {
  QLD: "Queensland",
  NSW: "New South Wales",
  VIC: "Victoria",
  SA: "South Australia",
};

export type AvailabilityStatus =
  | "in_stock"
  | "limited_stock"
  | "call_to_confirm"
  | "special_order"
  | "unavailable";

export type PriceDisplayMode =
  | "show_price"
  | "hide_price_request_quote"
  | "from_price"
  | "trade_price_only"
  | "contact_for_price";

// Full customer-facing message shown on product pages. Friendly, calm wording —
// never harsh ("error", "failed", "no stock", "cannot buy").
export const AVAILABILITY_MESSAGES: Record<AvailabilityStatus, string> = {
  in_stock: "Available at your selected location",
  limited_stock: "Limited availability — call to confirm",
  call_to_confirm: "Please confirm availability before visiting or pickup",
  special_order: "Available by special order",
  unavailable: "Not currently available at this location",
};

// Friendly, actionable sub-message shown beneath the headline. Keeps every
// state from feeling like a dead end.
export const AVAILABILITY_SUBMESSAGES: Record<AvailabilityStatus, string | null> = {
  in_stock: null,
  limited_stock: "Add to cart or request a quote and our team can confirm stock for you.",
  call_to_confirm: "Our team can confirm availability and current pricing for you.",
  special_order: "Request a quote and we'll confirm lead time for your order.",
  unavailable: "You can still request a stock update or check another branch.",
};

// Statuses that warrant a customer-facing helper/compliance note.
// Normal in-stock products stay clean (no loud notes by default).
export function statusNeedsNote(status: AvailabilityStatus): boolean {
  return status !== "in_stock";
}

// Compact label for product cards, e.g. "In stock in QLD".
// Kept in sync with AVAILABILITY_MESSAGES so the badge never contradicts the
// message (e.g. a "Call to confirm" status must NOT read "Limited").
export function compactAvailabilityLabel(
  status: AvailabilityStatus,
  state: AustralianState,
): string {
  switch (status) {
    case "in_stock":
      return `In stock in ${state}`;
    case "limited_stock":
      return `Limited in ${state}`;
    case "call_to_confirm":
      return `Call to confirm — ${state}`;
    case "special_order":
      return `Special order — ${state}`;
    case "unavailable":
      return `Sold Out — ${state}`;
  }
}

// Tailwind badge variant per status (uses existing semantic tokens only).
export function availabilityBadgeClass(status: AvailabilityStatus): string {
  switch (status) {
    case "in_stock":
      return "bg-primary/10 text-primary border-primary/20";
    case "limited_stock":
    case "call_to_confirm":
      return "bg-accent/10 text-accent-foreground border-accent/30";
    case "special_order":
      return "bg-secondary/10 text-secondary-foreground border-secondary/20";
    case "unavailable":
      return "bg-destructive/10 text-destructive border-destructive/30";
  }
}

// Fallback shown when no availability row exists for the selected location.
export const NO_ROW_FALLBACK_MESSAGE =
  "Contact us to confirm availability and current price for your selected location.";

// Shown when the customer has not picked a location yet.
export const NO_LOCATION_MESSAGE =
  "Select your location to check availability.";

// Compliance / trust note.
export const COMPLIANCE_NOTE =
  "Stock availability may vary by location. Please wait for confirmation before pickup or delivery.";

export interface PublicAvailabilityRow {
  product_handle: string;
  state: string;
  location_name: string;
  availability_status: AvailabilityStatus;
  price_display_mode: PriceDisplayMode;
  price_unit: string | null;
  regular_price: number | null;
  sale_price: number | null;
  quantity: number | null;
  show_quantity_publicly: boolean;
  note: string | null;
  /** NEW-arrival badge: only true when active for this location. */
  is_new_arrival?: boolean | null;
  /** Customer-safe badge label (e.g. "NEW"); null when badge inactive. */
  new_badge_label?: string | null;
  last_updated: string;
}

function formatMoney(value: number): string {
  return `$${value.toFixed(2)}`;
}

export interface PriceDisplayResult {
  text: string; // main price text or call-to-action
  label: string | null; // e.g. "QLD price"
  isCta: boolean; // true when it's "Request a quote" / trade etc.
}

// Resolves what to show for price given the public availability row.
export function resolvePriceDisplay(
  row: PublicAvailabilityRow | null,
  state: AustralianState | null,
  basePrice: number | null,
): PriceDisplayResult {
  const label = state ? `${state} price` : null;
  const unit = row?.price_unit ? ` ${row.price_unit}` : "";

  if (row) {
    switch (row.price_display_mode) {
      case "hide_price_request_quote":
        return { text: "Request a quote", label: null, isCta: true };
      case "trade_price_only":
        return { text: "Contact for trade pricing", label: null, isCta: true };
      case "contact_for_price":
        return { text: "Contact us for current price", label: null, isCta: true };
      case "from_price": {
        const v = row.sale_price ?? row.regular_price;
        if (v != null) return { text: `From ${formatMoney(v)}${unit}`, label, isCta: false };
        break;
      }
      case "show_price": {
        const v = row.sale_price ?? row.regular_price;
        if (v != null) return { text: `${formatMoney(v)}${unit}`, label, isCta: false };
        break;
      }
    }
  }

  // Fallback to base price.
  if (basePrice != null && basePrice > 0) {
    return { text: formatMoney(basePrice), label, isCta: false };
  }

  return { text: "Contact us for current price", label: null, isCta: true };
}

// ─────────────────────────────────────────────────────────────────────────
// Central numeric price resolver — single source of truth for the active
// unit price at the selected location. Used by the availability display, the
// product title price, the calculator, cart and quote snapshots so they never
// disagree.
//
// Resolution order:
//   1. Location sale price
//   2. Location regular price
//   3. Inherited website/base price (no custom branch override)
//   4. null → "Contact us for current price"
// ─────────────────────────────────────────────────────────────────────────
export interface ResolvedPrice {
  /** Numeric unit price to use everywhere, or null when price must be requested. */
  amount: number | null;
  /** Price unit, e.g. "per m²" / "per board" (null when inherited base or contact). */
  unit: string | null;
  /** Selected state, or null when none chosen. */
  state: AustralianState | null;
  /** Display label e.g. "QLD price". */
  label: string | null;
  /** True when no numeric price is available (request a quote / contact). */
  isContact: boolean;
  /** Source of the resolved price. */
  source: "location" | "inherited" | "none";
}

export function resolveActivePrice(
  row: PublicAvailabilityRow | null,
  state: AustralianState | null,
  basePrice: number | null,
): ResolvedPrice {
  const label = state ? `${state} price` : null;

  if (row) {
    const mode = row.price_display_mode;
    if (mode === "show_price" || mode === "from_price") {
      const v = row.sale_price ?? row.regular_price;
      if (v != null && v >= 0) {
        return { amount: v, unit: row.price_unit, state, label, isContact: false, source: "location" };
      }
    } else {
      // hide_price_request_quote / trade_price_only / contact_for_price
      return { amount: null, unit: null, state, label: null, isContact: true, source: "none" };
    }
  }

  // No custom branch override (or row had no usable price) → inherited website default.
  if (basePrice != null && basePrice > 0) {
    return { amount: basePrice, unit: null, state, label, isContact: false, source: "inherited" };
  }

  return { amount: null, unit: null, state, label: null, isContact: true, source: "none" };
}

// ─────────────────────────────────────────────────────────────────────────
// Central availability BEHAVIOUR resolver — single source of truth for how a
// product/location status should behave for the customer (not just what text
// to show). Every surface (product page, cards, cart, calculator) should use
// this so behaviour never disagrees.
//
// Behaviour matrix:
//   in_stock       → normal: add-to-cart enabled, no notes
//   limited_stock  → add-to-cart enabled, helpful "call to confirm" message
//   call_to_confirm→ quote-first: add-to-cart disabled, primary = Request Quote
//   special_order  → quote-first: add-to-cart disabled, primary = Request Quote
//   unavailable    → SOLD OUT: add-to-cart disabled, "Request Stock Update"
// ─────────────────────────────────────────────────────────────────────────
export interface AvailabilityBehaviour {
  /** Resolved status, or null when no location / no row (inherited default). */
  status: AvailabilityStatus | null;
  /** True → treat as Sold Out / Stock Out. */
  isSoldOut: boolean;
  /** True → customer can add the product to the cart. */
  canAddToCart: boolean;
  /** True → primary action should be Request Quote / Contact Branch. */
  quoteFirst: boolean;
  /** Short label for the Add-to-Cart button when gated (e.g. "Sold Out"). */
  cartButtonLabel: string | null;
  /** Corner badge label for cards/product page (e.g. "Sold Out"), or null. */
  soldOutBadge: string | null;
  /** Customer-facing headline message, or null when the product should stay clean. */
  message: string | null;
  /** True when a helper/compliance note is warranted. */
  needsNote: boolean;
}

/**
 * Resolves customer-facing behaviour from a public availability row.
 *
 * @param row         sanitized public row (null = no branch override / inherited default)
 * @param hasLocation whether the customer has chosen a location
 * @param hasInheritedDefault whether a website/base price exists to fall back to
 */
export function resolveAvailabilityBehaviour(
  row: PublicAvailabilityRow | null,
  hasLocation: boolean,
  hasInheritedDefault: boolean,
): AvailabilityBehaviour {
  // No location chosen, or inherited default (no explicit branch override):
  // behave like a normal available product. Do NOT force scary notes.
  if (!hasLocation || !row) {
    const contactOnly = hasLocation && !row && !hasInheritedDefault;
    return {
      status: null,
      isSoldOut: false,
      canAddToCart: !contactOnly,
      quoteFirst: contactOnly,
      cartButtonLabel: null,
      soldOutBadge: null,
      message: contactOnly ? NO_ROW_FALLBACK_MESSAGE : null,
      needsNote: contactOnly,
    };
  }

  const status = row.availability_status;
  switch (status) {
    case "in_stock":
      return {
        status,
        isSoldOut: false,
        canAddToCart: true,
        quoteFirst: false,
        cartButtonLabel: null,
        soldOutBadge: null,
        message: null,
        needsNote: false,
      };
    case "limited_stock":
      return {
        status,
        isSoldOut: false,
        canAddToCart: true,
        quoteFirst: false,
        cartButtonLabel: null,
        soldOutBadge: null,
        message: AVAILABILITY_MESSAGES.limited_stock,
        needsNote: true,
      };
    case "call_to_confirm":
      return {
        status,
        isSoldOut: false,
        canAddToCart: false,
        quoteFirst: true,
        cartButtonLabel: "Request Quote",
        soldOutBadge: null,
        message: AVAILABILITY_MESSAGES.call_to_confirm,
        needsNote: true,
      };
    case "special_order":
      return {
        status,
        isSoldOut: false,
        canAddToCart: false,
        quoteFirst: true,
        cartButtonLabel: "Request Quote",
        soldOutBadge: "Special Order",
        message: AVAILABILITY_MESSAGES.special_order,
        needsNote: true,
      };
    case "unavailable":
      return {
        status,
        isSoldOut: true,
        canAddToCart: false,
        quoteFirst: true,
        cartButtonLabel: "Sold Out",
        soldOutBadge: "Sold Out",
        message: AVAILABILITY_MESSAGES.unavailable,
        needsNote: true,
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// GLOBAL (no-location) availability summary — computed from every branch row
// for a product across all four states. Lets the site show a correct global
// state even before the customer picks a location.
// ─────────────────────────────────────────────────────────────────────────
export const ALL_STATES: AustralianState[] = ["QLD", "NSW", "VIC", "SA"];

export type GlobalAvailabilityStatus =
  | "globally_sold_out"
  | "available_some_locations"
  | "limited_some_locations"
  | "special_order_some_locations"
  | "confirm_required_some_locations"
  | "select_location_to_check";

export interface GlobalAvailabilitySummary {
  status: GlobalAvailabilityStatus;
  /** True → every one of the four states is explicitly unavailable. */
  isGloballySoldOut: boolean;
  /** Customer-facing headline message. */
  message: string;
  /** True → at least one location has usable availability. */
  hasAnyAvailable: boolean;
}

// Prefer the "most available" status when a state has several locations.
const STATUS_RANK: Record<AvailabilityStatus, number> = {
  in_stock: 5,
  limited_stock: 4,
  special_order: 3,
  call_to_confirm: 2,
  unavailable: 1,
};

function betterStatus(
  a: AvailabilityStatus | undefined,
  b: AvailabilityStatus,
): AvailabilityStatus {
  if (!a) return b;
  return STATUS_RANK[b] > STATUS_RANK[a] ? b : a;
}

/**
 * Computes the global availability summary from all branch rows for a product.
 * A product is only globally Sold Out when all four states have an explicit
 * unavailable row. Otherwise a "select your location" style prompt is returned.
 */
export function resolveGlobalAvailability(
  rows: PublicAvailabilityRow[] | null | undefined,
): GlobalAvailabilitySummary {
  if (!rows || rows.length === 0) {
    return {
      status: "select_location_to_check",
      isGloballySoldOut: false,
      message: NO_LOCATION_MESSAGE,
      hasAnyAvailable: false,
    };
  }

  const byState = new Map<string, AvailabilityStatus>();
  for (const r of rows) {
    if (!ALL_STATES.includes(r.state as AustralianState)) continue;
    byState.set(r.state, betterStatus(byState.get(r.state), r.availability_status));
  }

  const statuses = Array.from(byState.values());
  const allFourCovered = ALL_STATES.every((s) => byState.has(s));
  const allUnavailable = statuses.length > 0 && statuses.every((s) => s === "unavailable");

  if (allFourCovered && allUnavailable) {
    return {
      status: "globally_sold_out",
      isGloballySoldOut: true,
      message: "Not currently available across our locations.",
      hasAnyAvailable: false,
    };
  }

  const hasAnyAvailable = statuses.some((s) => s === "in_stock" || s === "limited_stock");

  if (statuses.includes("in_stock")) {
    return { status: "available_some_locations", isGloballySoldOut: false, message: NO_LOCATION_MESSAGE, hasAnyAvailable: true };
  }
  if (statuses.includes("limited_stock")) {
    return { status: "limited_some_locations", isGloballySoldOut: false, message: NO_LOCATION_MESSAGE, hasAnyAvailable: true };
  }
  if (statuses.includes("special_order")) {
    return { status: "special_order_some_locations", isGloballySoldOut: false, message: NO_LOCATION_MESSAGE, hasAnyAvailable: false };
  }
  if (statuses.includes("call_to_confirm")) {
    return { status: "confirm_required_some_locations", isGloballySoldOut: false, message: NO_LOCATION_MESSAGE, hasAnyAvailable: false };
  }

  return { status: "select_location_to_check", isGloballySoldOut: false, message: NO_LOCATION_MESSAGE, hasAnyAvailable };
}

// ─────────────────────────────────────────────────────────────────────────
// Postcode → state inference (for cart / quote location validation).
// Uses the standard Australian postcode ranges for the served states only.
// ─────────────────────────────────────────────────────────────────────────
export function postcodeToState(postcode: string | null | undefined): AustralianState | null {
  if (!postcode) return null;
  const digits = postcode.trim().replace(/\D/g, "");
  if (digits.length < 3) return null;
  const n = parseInt(digits.slice(0, 4).padEnd(4, "0"), 10);
  if (Number.isNaN(n)) return null;
  // NSW: 1000–2599, 2619–2899, 2921–2999
  if ((n >= 1000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return "NSW";
  // VIC: 3000–3999, 8000–8999
  if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return "VIC";
  // QLD: 4000–4999, 9000–9999
  if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return "QLD";
  // SA: 5000–5799
  if (n >= 5000 && n <= 5799) return "SA";
  return null;
}

export const OUT_OF_SERVICE_MESSAGE =
  "We currently service QLD, NSW, VIC and SA. Please contact us for availability.";

// Warehouse/pickup locations available per state (short names).
export const WAREHOUSES_BY_STATE: Record<AustralianState, string[]> = {
  QLD: ["Virginia", "Darra"],
  NSW: ["Mount Druitt"],
  VIC: ["Truganina"],
  SA: ["Ridleyton"],
};

export function warehousesForState(state: AustralianState | null): string[] {
  if (!state) return ["Virginia", "Darra", "Mount Druitt", "Truganina", "Ridleyton"];
  return WAREHOUSES_BY_STATE[state] ?? [];
}

// Normalises a product handle/slug into a stable key used across the
// availability system (strips the local-link prefix and leading slash).
export function canonicalHandle(handle: string | null | undefined): string {
  if (!handle) return "";
  return handle.replace(/^__local__/, "").replace(/^\//, "").trim();
}

// Plain (non-hook) reader for the selected location, for use in form payloads.
export function getSelectedLocationPayload(): {
  selectedLocationState: AustralianState | null;
  selectedLocationName: string | null;
} {
  try {
    const v = localStorage.getItem("st_selected_location");
    if (v === "QLD" || v === "NSW" || v === "VIC" || v === "SA") {
      return { selectedLocationState: v, selectedLocationName: STATE_LABELS[v] };
    }
  } catch {
    /* ignore */
  }
  return { selectedLocationState: null, selectedLocationName: null };
}
